import React from 'react';
import { Link } from 'react-router-dom';
import { DonationProvider } from '@joindeed/calculate-fees';

import deedIcon from 'assets/logo/stacked/deed-logo-stacked-white.svg';
import nfgIcon from 'assets/onboarding/nfg.svg';
import ppdIcon from 'assets/onboarding/ppd.svg';
import giveIndiaIcon from 'assets/onboarding/giveindia.svg';
import globalGivingIcon from 'assets/onboarding/globalgiving.svg';
import ppgfIcon from 'assets/onboarding/ppgf.svg';
import stripeIcon from 'assets/onboarding/stripe.svg';
import Organization, { NonprofitEntityType } from 'entities/organization/model';
import config from 'config';
import { useQuery } from 'entities/useQuery';
import DonationsApi from 'entities/donation/api';
import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { black } from 'utils/color-styles';

export const useContent = (
  donationProvider: DonationProvider | null | undefined,
  organization: Organization | undefined,
  selfVerified?: boolean
) => {
  const { data } = useQuery(
    () =>
      DonationsApi.fetchDonationProviderDataForNonprofit(
        organization?.donateViaNonprofitId || ''
      ).toPromise(),
    null,
    organization?.donateViaNonprofitId,
    !organization?.donateViaNonprofitId
  );

  const donateViaNonprofitName = data?.donateViaNonprofits?.[0]?.name;

  if (donateViaNonprofitName) {
    const accountType =
      data.donationProvider === DonationProvider.PayPal
        ? 'PayPal Giving Fund'
        : data.donationProvider;
    const header = `Donations will go to the ${accountType} account for ‘${donateViaNonprofitName}’ and include information informing them that the donation is for ‘${organization?.name}’`;

    if (data.donationProvider === DonationProvider.PayPal) {
      return {
        enrolled: true,
        header,
        body: (
          <>
            You can register with PPGF here if you would like to receive donations to a different
            PPGF account. If your organization needs to be attached to a different PPGF account than
            listed above please contact{' '}
            <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>
              {config.nonprofitSuccessTeamEmail}
            </a>{' '}
            and our team will work to make sure that your Deed account is attached to the correct
            PPGF account.
          </>
        ),
        tooltip:
          'Paypal Giving Fund (PPGF) is a donor-advised fund, which holds and then disburses funds to nonprofits like yours.',
        link: `https://www.paypal.com/${
          organization?.locationObject?.countryCode?.toLowerCase() || 'us'
        }/webapps/mpp/givingfund/joinppgf`,
        ctaText: 'Enroll with PayPal Giving Fund',
        logo: ppgfIcon,
      };
    }
    if (data.donationProvider === DonationProvider.NFG) {
      return {
        enrolled: true,
        header,
        body: (
          <>
            You can view all donations to your chapter on the{' '}
            <Link to="/donations"> Donations tab</Link>. If this information is not correct or if
            you have any questions please contact
            <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>
              {config.nonprofitSuccessTeamEmail}
            </a>
            .
          </>
        ),
        tooltip:
          'NFG is a hybrid nonprofit/for-profit, which uses technology to securely and efficiently distribute donations to domestic US charities.',
        ctaText: 'Learn More',
        link: 'https://www.notion.so/joindeed/NCES-Schools-and-Network-for-Good-10c6e4e82609470aa31c04adeab1a6ef',
        logo: nfgIcon,
      };
    }
  }

  if (
    !selfVerified &&
    organization?.verificationStatus === 'inactive' &&
    !organization?.donationProviders?.NFG
  ) {
    return {
      enrolled: false,
      header: 'User Verification with PayPal',
      body: (
        <>
          <ModalTypography>
            Deed works in partnership with <b>PayPal Giving Fund (PPGF)</b> to verify both
            organizations and the individuals associated with those organizations. To finish
            onboarding, <b>public charities are required to enroll in PPGF</b>.
          </ModalTypography>
          <ModalTypography>
            <b>Your PayPal account status is currently marked Inactive.</b> This may be due to
            missing account settings or incorrect information.
          </ModalTypography>
          <ModalTypography>
            <b>Please use the Contact PayPal Support</b> button to work with PayPal to return your
            account to Active status.
          </ModalTypography>
        </>
      ),
      tooltip:
        'PayPal Giving Fund (PPGF) is a donor-advised fund, which holds and then disburses funds to nonprofits like yours. If you are fully enrolled with PPGF, you will receive donations directly into your account on a monthly basis.',
      link: `https://www.paypal.com/${
        organization?.locationObject?.countryCode?.toLowerCase() || 'us'
      }/webapps/mpp/givingfund/joinppgf`,
      ctaText: 'Contact PayPal Support',
      logo: ppgfIcon,
    };
  }

  if (organization?.paypalCharityType === 'PPGF_ENROLLED') {
    return {
      enrolled: true,
      header: `You're all set! We disburse donations to you through our partner, PayPal Giving Fund.`,
      body: selfVerified ? (
        <>
          <ModalTypography>
            You’ve successfully verified your account using trusted credentials between your Deed
            and PayPal accounts.
          </ModalTypography>
          <ModalTitle style={{ color: black }}>Disbursement Overview</ModalTitle>
          <ModalTypography>
            Since you are enrolled with PPGF, you will receive employee giving and company matches
            directly into your account on a monthly basis.
          </ModalTypography>
          <ModalTypography>
            <b>Please note</b>, some company partners may utilize additional disbursements methods
            for other programs. Be sure to check company guidelines for additional information about
            disbursement methods.
          </ModalTypography>
        </>
      ) : (
        `Since you're enrolled with PPGF, you will receive donations directly into your account on a monthly basis. To learn more about PPGF and how money is disbursed to you, continue below.`
      ),
      tooltip:
        'Paypal Giving Fund (PPGF) is a donor-advised fund, which holds and then disburses funds to nonprofits like yours.',
      link: `https://www.paypal.com/${
        organization?.locationObject?.countryCode?.toLowerCase() || 'us'
      }/givingfund/home`,
      ctaText: selfVerified ? 'Next' : 'Learn more',
      logo: ppgfIcon,
    };
  }

  if (organization?.paypalCharityType === 'DIRECT_MODEL_ENROLLED') {
    return {
      enrolled: true,
      header: `You're all set! We disburse donations to you through our partner, PayPal Direct.`,
      body: `Since you’re enrolled with PayPal, you will receive donations directly to your PayPal
          account. To learn more about PayPal Direct and how money is disbursed to you, click
          below.`,
      tooltip: `If you're a nonprofit that is enrolled with PayPal, you can also log into your PayPal account to review donations received.`,
      link: ` https://www.paypal.com/${
        organization?.locationObject?.countryCode?.toLowerCase() || 'us'
      }/non-profit/fundraising/fundraising-online`,
      ctaText: 'Learn more',
      logo: ppdIcon,
    };
  }

  if (
    organization?.payPalModel === 'DIRECT' ||
    (!donationProvider && organization?.isInPayPalDirectEligibleCountry())
  ) {
    return {
      enrolled: false,
      title: 'PayPal Direct',
      header: `Enroll with PayPal Direct.`,
      body: 'You can check your country-specific PayPal website to learn about enrolling your nonprofit as a charity. Refer to the PayPal Customer Support for questions or issues.',
      tooltip:
        "You'll need to register your account with PayPal as a nonprofit in order to receive donations.",
      link: `https://www.paypal.com/${
        organization?.locationObject?.countryCode?.toLowerCase() || 'us'
      }/cshelp/personal`,
      ctaText: 'Enroll with PayPal Direct',
      logo: ppdIcon,
    };
  }

  if (
    organization?.payPalModel === 'PPGF' ||
    (!donationProvider &&
      organization?.isInPPGFEligibleCountry() &&
      organization?.nonprofitEntityType === NonprofitEntityType.nonprofit)
  ) {
    return {
      enrolled: false,
      title: 'PayPal Giving Fund',
      header: selfVerified ? `Enroll with PayPal Giving Fund` : `Complete User Verification`,
      body: selfVerified ? (
        'Register below to receive electronic funds transferred instantly from the PayPal Giving Fund! After finishing the enrollment process, kindly return to proceed with the onboarding flow.'
      ) : (
        <>
          For <b>User Verification</b>, Deed works in partnership with{' '}
          <b>PayPal Giving Fund (PPGF)</b> to verify both organizations and the individuals
          associated with those organizations. To finish onboarding,{' '}
          <b>
            public charities are required to enroll in PPGF. After finishing the enrollment process,
            kindly return to proceed with the onboarding flow.
          </b>
        </>
      ),
      tooltip:
        'PayPal Giving Fund (PPGF) is a donor-advised fund, which holds and then disburses funds to nonprofits like yours. If you are fully enrolled with PPGF, you will receive donations directly into your account on a monthly basis.',
      link: `https://www.paypal.com/${
        organization?.locationObject?.countryCode?.toLowerCase() || 'us'
      }/webapps/mpp/givingfund/joinppgf`,
      ctaText: selfVerified ? 'Enroll with PayPal Giving Fund' : 'Complete User Verification',
      logo: ppgfIcon,
    };
  }

  if (donationProvider === DonationProvider.GlobalGiving) {
    return {
      enrolled: true,
      title: 'GlobalGiving',
      header: `You're all set! We disburse donations to you through our partner, GlobalGiving.`,
      body: 'GlobalGiving disburses funds at the end of the month after donations are received. To learn more about GlobalGiving and how money is disbursed to you, click below.',
      tooltip:
        'GlobalGiving is 501(c)(3) non-profit organization based in the United States that provides a global crowdfunding platform for grassroots charitable projects.',
      link: 'https://www.globalgiving.org/nonprofit-partner-handbook/start-fundraising/',
      ctaText: 'Learn more',
      logo: globalGivingIcon,
    };
  }

  if (
    donationProvider === DonationProvider.NFG ||
    (!donationProvider &&
      organization?.nonprofitEntityType === NonprofitEntityType.school &&
      organization?.locationObject.countryCode === 'US')
  ) {
    return {
      enrolled: donationProvider === DonationProvider.NFG,
      title: 'Network for Good',
      header:
        donationProvider === DonationProvider.NFG
          ? `You’re all set! We disburse donations to you through our partner, NFG.`
          : 'Register with NFG',
      body:
        donationProvider === DonationProvider.NFG
          ? organization?.disbursementMethods?.NFG === 'ACH'
            ? `You've successfully opted in for NFG's direct deposit feature, ensuring a streamlined and efficient disbursement process. With this arrangement, you can enjoy the convenience of accessing funds instantly on the 15th of each month, making your financial operations smoother and more effective.`
            : `Have you received a donation from NFG in the past? Enhance your disbursement process with NFG's direct deposit option. By default, checks are mailed on the 15th of each month. For faster access and to eliminate mailing concerns, consider setting up direct deposit (EFT). Experience a seamless and efficient disbursement process with immediate fund access on the 15th of every month. Make the transition today!`
          : 'Network for Good is a 501(c)(3) charity that operates a technology-enabled donor advised fund which enables donors to raise and direct funds to their favorite charities. Since 2001, we have disbursed over $4.5 billion to over 400,000 unique charities across the United States.',
      tooltip:
        'NFG is a hybrid nonprofit/for-profit, which uses technology to securely and efficiently distribute donations to domestic US charities.',
      link:
        donationProvider === DonationProvider.NFG
          ? 'https://www.notion.so/joindeed/NCES-Schools-and-Network-for-Good-10c6e4e82609470aa31c04adeab1a6ef'
          : 'https://www.nfggive.org/account/register',
      ctaText:
        donationProvider === DonationProvider.NFG
          ? organization?.disbursementMethods?.NFG === 'ACH'
            ? 'Learn more'
            : 'Set Up Direct Deposit'
          : 'Register with NFG',
      logo: nfgIcon,
    };
  }

  if (
    donationProvider === DonationProvider.GiveIndia ||
    (!donationProvider && organization?.locationObject?.countryCode === 'IN')
  ) {
    return {
      enrolled: true,
      title: 'GiveIndia',
      header: `You're all set! We disburse donations to you through our partner, GiveIndia.`,
      body: `GiveIndia disburses donations of a particular month on the 10th of the following month. This is applicable for donations made in India. For those in the US/UK, donations made by the 15th of the month are disbursed to the NGO on the 10th of the following month.`,
      tooltip: 'GiveIndia is Deed’s trusted partner for India-based organizations.',
      link: 'https://cfstatic.give.do/dffac2b1-f40f-4d69-8686-3ec57a7d14ca.pdf',
      ctaText: 'Learn more',
      logo: giveIndiaIcon,
    };
  }

  if (donationProvider === DonationProvider.Stripe) {
    return {
      enrolled: true,
      title: 'Stripe',
      header: `You're all set! We disburse donations to you through our partner, Stripe.`,
      body: `Stripe makes it easy to accept one-time or recurring online donations to support nonprofit communities and causes.`,
      tooltip:
        'Stripe is widely used by nonprofit organizations that are looking to collect donations online. While it allows you to accept both one-time and recurring donations.',
      link: 'https://cfstatic.give.do/dffac2b1-f40f-4d69-8686-3ec57a7d14ca.pdf',
      ctaText: 'Learn more',
      logo: stripeIcon,
    };
  }

  if (donationProvider === DonationProvider.CAF) {
    return {
      enrolled: true,
      title: '',
      header: 'CAF Payment Disclosure for Nonprofits',
      body: (
        <>
          Your organization will receive donations through PayPal Giving Fund (PPGF), an
          IRS-registered 501(c)(3) charity. PPGF grants funds to CAF America (EIN 431634280), also
          an IRS-registered 501(c)(3) charity, which then distributes the funds to your nonprofit as
          recommended by the donor. Typically, funds are transferred within 22-52 days of the
          original donation.
        </>
      ),
      tooltip: '',
      link: `mailto:${config.nonprofitSuccessTeamEmail}`,
      ctaText: 'Contact us',
      logo: deedIcon,
    };
  }

  return {
    enrolled: false,
    title: '',
    header: 'You are not set up to receive donations',
    body: (
      <>
        To set up donations for your organization please reach out to our Nonprofit Success Team via{' '}
        <a href={`mailto:${config.nonprofitSuccessTeamEmail}`}>
          {config.nonprofitSuccessTeamEmail}
        </a>
        . In the meantime, you can still use Deed to organize volunteering opportunities.
      </>
    ),
    tooltip: '',
    link: `mailto:${config.nonprofitSuccessTeamEmail}`,
    ctaText: 'Contact us',
    logo: deedIcon,
  };
};
